import {
  filter,
  flatten,
  groupBy,
  intersection,
  map,
  pipe,
  pluck,
  prop,
  reduce,
  sortBy,
  toPairs,
  uniq,
  uniqBy,
} from "ramda";

import { ORDERED_CATEGORIES } from "helpers/schedule/roles";
import { IScheduleFilters } from "types/reducers";
import { IPeople, IRole, IUsers, Category } from "types/schedules";

function getUserRoles(users: IUsers): IRole[] {
  return pipe<IUsers, IRole[][], IRole[], IRole[], IRole[]>(
    pluck("roles"),
    flatten as (roles: IRole[][]) => IRole[],
    uniqBy(prop("uuid")),
    sortBy(prop("name"))
  )(users);
}

export interface ICategoryWithRoles {
  name: string;
  roles: string[];
}

function applyFilters({ roles }: IScheduleFilters, people: IPeople): IPeople {
  if (roles.length === 0) {
    // No filters are set, return everything
    return people;
  } else {
    return reduce(
      (filteredPeople, person) => {
        return person.assignee.match({
          Just: (assignee) => {
            const assigneeRoles = pluck("name", assignee.roles);
            if (!!intersection(assigneeRoles, roles).length) {
              return filteredPeople.concat(person);
            }
            return filteredPeople;
          },
          Nothing: () => {
            // always include Unassigned person, however, filter their shifts
            const filteredShifts = filter(
              (shift) => roles.includes(shift.role.name),
              person.shifts
            );
            return filteredPeople.concat({
              ...person,
              shifts: filteredShifts,
            });
          },
        });
      },
      [] as IPeople,
      people
    );
  }
}

function getCategoriesWithRoles(users: IUsers): ICategoryWithRoles[] {
  return pipe<
    IUsers,
    IRole[],
    { [index: string]: IRole[] },
    Array<[string, IRole[]]>,
    ICategoryWithRoles[],
    ICategoryWithRoles[]
  >(
    getUserRoles,
    groupBy(prop("category")),
    toPairs,
    map(([name, rolesInCat]) => ({
      name,
      roles: uniq(map(prop("name"), rolesInCat)) as string[],
    })),
    sortBy((catGroup: ICategoryWithRoles) =>
      ORDERED_CATEGORIES.indexOf(catGroup.name as Category)
    )
  )(users);
}

export { applyFilters, getCategoriesWithRoles };
